import { EMAIL_LOGIN_LINK_PATH } from "@src/appV2/Auth/AuthRedirect/paths";
import { mapLegacyRoute } from "@src/appV2/redesign/PushNotifications/legacyRouteMapper";
import { routes } from "@src/appV2/redesign/utils/routes";
import { type ReactNode, useEffect } from "react";
import { useHistory } from "react-router-dom";

import { initializeDeepLinkHandler } from "../utils/deepLinkHandler";
import { Platform } from "../utils/platform";

const AUTH_DEEP_LINK_PREFIXES = [EMAIL_LOGIN_LINK_PATH, routes.loginVerify];

interface DeepLinkProviderProps {
  children: ReactNode;
}

/**
 * Provider that initializes deep link handling for the mobile app.
 * This should be mounted at the root level and never unmount to ensure
 * deep link listeners persist throughout the app lifecycle.
 */
export function DeepLinkProvider({ children }: DeepLinkProviderProps) {
  const history = useHistory();

  useEffect(() => {
    if (!Platform.isMobile) {
      return undefined;
    }

    const cleanup = initializeDeepLinkHandler((path: string) => {
      // path is already normalised to "/pathname?search" by normaliseDeepLink.
      // TEMPORARY: Braze push sync — remove when legacy facility app is sunset.
      // Braze campaigns use legacy route paths (e.g. /chat?channelUrl=...) that
      // need translating to the redesigned app's routes. isChatRedesignEnabled
      // is always true on mobile (see useIsChatRedesignEnabled) so we can pass
      // it as a constant here without pulling in the feature-flag hook.
      const mapped = mapLegacyRoute(path, { isChatRedesignEnabled: true });
      const finalPath = mapped ?? path;

      // Auth deep links (magic link callbacks, login verification) are one-time
      // navigation events. Using replace instead of push prevents the login page
      // from remaining in the history stack, which would cause the user to land
      // back on the login screen when swiping back after authentication.
      const isAuthLink = AUTH_DEEP_LINK_PREFIXES.some((prefix) => finalPath.startsWith(prefix));

      if (isAuthLink) {
        history.replace(finalPath);
      } else {
        history.push(finalPath);
      }
    });

    return () => {
      cleanup();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps - only run once on mount

  return children;
}
